import React, { useState, useEffect } from 'react';
import { runCode,getOptimalCode,getSpaceComplexity,getTimeComplexity } from './api';
import ModeToggle from './ModeToggle';

const App = () => {
  const [selectedLanguage, setSelectedLanguage] = useState('javascript');
  const [code, setCode] = useState('');
  const [output, setOutput] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [isMobileMode, setIsMobileMode] = useState(false); // NEW: UI mode toggle
  

  const languages = [
    { value: 'javascript', label: 'JavaScript', extension: '.js' },
    { value: 'python', label: 'Python', extension: '.py' },
    { value: 'java', label: 'Java', extension: '.java' },
    { value: 'cpp', label: 'C++', extension: '.cpp' },
    { value: 'c', label: 'C', extension: '.c' }
  ];

  const getDefaultCode = (language) => {
    switch (language) {
      case 'javascript':
        return 'console.log("Hello Coder!");  \n \n \n \n \n \n \n \n \n \n  // Please provide your input hear: ';
      case 'java':
        return 'import java.util.Scanner;\n\npublic class Main {\n    public static void main(String[] args) {\n        Scanner scanner = new Scanner(System.in);\n        System.out.println("Hello Coder!");\n        scanner.close();\n    }\n}  \n \n \n \n \n \n \n \n \n \n  // Please provide your input hear: ';
      case 'cpp':
        return '#include <iostream>\n#include <string>\nusing namespace std;\n\nint main() {\n    cout << "Hello Coder!" << endl;\n    return 0;\n}   \n \n \n \n \n \n \n \n \n \n  // Please provide your input hear: ';
      case 'c':
        return '#include <stdio.h>\n#include <string.h>\n\nint main() {\n    printf("Hello Coder!\\n");\n    return 0;\n}  \n \n \n \n \n \n \n \n \n \n  // Please provide your input hear: ';
      case 'python':
        return 'print("Hello Coder!")    \n \n \n \n \n \n \n \n \n \n  // Please provide your input hear: ';
      default:
        return '';
    }
  };

  const handleRun = async () => {
    setIsRunning(true);
    setOutput('Running code...');
    try {
      const result = await runCode(code,selectedLanguage);
      setOutput(result);
    } catch (err) {
      setOutput('Error running code');
    }
    setIsRunning(false);
  };

  const handleTimeComplexity = async () => {
    const result = await getTimeComplexity(code);
    setOutput(result);
  };

  const handleSpaceComplexity = async () => {
    const result = await getSpaceComplexity(code);
    setOutput(result);
  };

  const handleOptimal = async () => {
    const result = await getOptimalCode(code);
    setOutput(result);
  };

  // Handle language change and set default code
  const handleLanguageChange = (e) => {
    const newLanguage = e.target.value;
    setSelectedLanguage(newLanguage);
    setCode(getDefaultCode(newLanguage));
    setOutput('Click "Run Code" to see output...');
  };

  // Set initial default code when component mounts
  useEffect(() => {
    setCode(getDefaultCode(selectedLanguage));
    setOutput('Click "Run Code" to see output...');
  }, [selectedLanguage]);

  
  const copyCode = () => {
    navigator.clipboard.writeText(code);
    const notification = document.createElement('div');
    notification.textContent = 'Code copied to clipboard!';
    notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-4 py-2 rounded shadow-lg z-50';
    document.body.appendChild(notification);
    setTimeout(() => document.body.removeChild(notification), 2000);
  };

  const saveCodeToFile = () => {
    const element = document.createElement('a');
    const file = new Blob([code], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = 'code.txt';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    const notification = document.createElement('div');
    notification.textContent = 'Code saved to code.txt!';
    notification.className = 'fixed top-4 right-4 bg-blue-500 text-white px-4 py-2 rounded shadow-lg z-50';
    document.body.appendChild(notification);
    setTimeout(() => document.body.removeChild(notification), 2000);
  };

  const downloadCode = () => {
    const element = document.createElement('a');
    const file = new Blob([code], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `main${languages.find(lang => lang.value === selectedLanguage).extension}`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const getFileName = () => {
    return `main${languages.find(lang => lang.value === selectedLanguage).extension}`;
  };

  // Example code that demonstrates input handling
  const getExampleCode = (language) => {
    switch (language) {
      case 'javascript':
        return '// Example: Add two numbers\nconst input = "5 3"; // This simulates user input\nconst numbers = input.split(" ").map(Number);\nconst sum = numbers[0] + numbers[1];\nconsole.log(`Sum: ${sum}`);';
      case 'java':
        return 'import java.util.Scanner;\n\npublic class Main {\n    public static void main(String[] args) {\n        Scanner scanner = new Scanner(System.in);\n        \n        // Example: Add two numbers\n        System.out.print("Enter first number: ");\n        int a = scanner.nextInt();\n        System.out.print("Enter second number: ");\n        int b = scanner.nextInt();\n        \n        int sum = a + b;\n        System.out.println("Sum: " + sum);\n        \n        scanner.close();\n    }\n}';
      case 'cpp':
        return '#include <iostream>\nusing namespace std;\n\nint main() {\n    // Example: Add two numbers\n    int a, b;\n    cout << "Enter first number: "; cin >> a;\n    cout << "Enter second number: "; cin >> b;\n    \n    int sum = a + b;\n    cout << "Sum: " << sum << endl;\n    \n    return 0;\n}';
      case 'c':
        return '#include <stdio.h>\n\nint main() {\n    // Example: Add two numbers\n    int a, b;\n    printf("Enter first number: ");\n    scanf("%d", &a);\n    printf("Enter second number: ");\n    scanf("%d", &b);\n    \n    int sum = a + b;\n    printf("Sum: %d\\n", sum);\n    \n    return 0;\n}';
      case 'python':
        return '# Example: Add two numbers\na = int(input("Enter first number: "))\nb = int(input("Enter second number: "))\nsum_result = a + b\nprint(f"Sum: {sum_result}")';
      default:
        return '';
    }
  };

  const getLineCount = () => {
    return code.split('\n').length;
  };

  const getCharCount = () => {
    return code.length;
  };

  return (
    <div className="min-h-screen bg-dark-bg text-white">
      {/* Header */}
      <div className="bg-dark-panel border-b border-dark-border px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h1 className="text-2xl font-bold gradient-text">Quantum Compiler</h1>
            <p className="text-gray-400 text-sm">It is a Professional code editor with intelligent compilation</p>
          </div>
          <div className="text-gray-400 text-sm">v2.1.0</div>
        </div>
      </div>

      {/*Language changing*/}
      <div className="bg-dark-panel border-b border-dark-border px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <select
              value={selectedLanguage}
              onChange={handleLanguageChange}
              className="bg-dark-bg border border-dark-border text-white px-4 py-2 rounded-lg focus:outline-none focus:border-accent-purple"
            >
              {languages.map(lang => (
                <option key={lang.value} value={lang.value}>
                  {lang.label}
                </option>
              ))}
            </select>
          </div>
          {/*Run button */}
          <div className="flex items-center space-x-3">
            {/* Toggle UI mode button */}
            <ModeToggle isMobileMode={isMobileMode} setIsMobileMode={setIsMobileMode} />
            <button
              onClick={handleRun}
              disabled={isRunning}
              className="gradient-button text-white px-6 py-2 rounded-lg font-medium flex items-center space-x-2 disabled:opacity-50"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
              </svg>
              <span>{isRunning ? 'Running...' : 'Run Code'}</span>
            </button>
            
            <button 
            onClick={handleTimeComplexity}
              className="gradient-button text-white px-4 py-2 rounded-lg font-medium flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Time Complexity</span>
            </button>

          {/*Space complexcity */}  
            <button onClick={handleSpaceComplexity}
              className="gradient-button text-white px-4 py-2 rounded-lg font-medium flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              <span>Space Complexity</span>
            </button>
            {/*Optimal version */}
            <button onClick={handleOptimal}
              className="gradient-button text-white px-4 py-2 rounded-lg font-medium flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <span>Optimal Version</span>
            </button>
            
            <button
              onClick={() => {
                saveCodeToFile();
                copyCode();
              }}
              className="bg-white text-gray-800 px-4 py-2 rounded-lg font-medium flex items-center space-x-2 hover:bg-gray-100 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <span>Copy</span>
            </button>
            
            <button
              onClick={() => {
                saveCodeToFile();
                downloadCode();
              }}
              className="bg-white text-gray-800 px-4 py-2 rounded-lg font-medium flex items-center space-x-2 hover:bg-gray-100 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>Download</span>
            </button>
            
            <button 
              onClick={() => setCode(getExampleCode(selectedLanguage))}
              className="bg-white text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>Load Example</span>
            </button>
          </div>
        </div>
      </div>



      {/* Main Content */}
      <div className={isMobileMode ? "flex flex-col h-auto" : "flex h-screen"}>
        {/* Code Editor Panel */}
        <div className={isMobileMode ? "w-full bg-dark-panel flex flex-col" : "flex-1 bg-dark-panel border-r border-dark-border flex flex-col"}>
          <div className="bg-dark-bg border-b border-dark-border px-4 py-2 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="flex space-x-1">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              </div>
              <span className="text-sm text-gray-400">{getFileName()}</span>
            </div>
          </div>
          <div className="flex-1 p-4">
            <textarea
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className={isMobileMode ? "w-full h-40 bg-dark-bg text-white border border-dark-border rounded-lg p-4 code-editor resize-none focus:outline-none focus:border-accent-purple text-base" : "w-full h-full bg-dark-bg text-white border border-dark-border rounded-lg p-4 code-editor resize-none focus:outline-none focus:border-accent-purple"}
              placeholder="Enter your code here..."
              style={isMobileMode ? { fontSize: '1.1rem', minHeight: 120 } : {}}
            />
          </div>
          <div className="bg-dark-bg border-t border-dark-border px-4 py-2 text-xs text-gray-400">
            Lines: {getLineCount()} | Chars: {getCharCount()}
          </div>
        </div>
        {/* Console Output Panel */}
        <div className={isMobileMode ? "w-full bg-dark-panel flex flex-col" : "flex-1 bg-dark-panel flex flex-col"}>
          <div className="bg-dark-bg border-b border-dark-border px-4 py-2 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="text-sm text-gray-400">Console Output</span>
            </div>
          </div>
          <div className="flex-1 p-4">
            <div className={isMobileMode ? "bg-dark-bg border border-dark-border rounded-lg p-4 min-h-32 overflow-y-auto h-full" : "bg-dark-bg border border-dark-border rounded-lg p-4 h-full overflow-y-auto"}>
              <pre className="text-sm text-gray-300 whitespace-pre-wrap font-mono">{output}</pre>
            </div>
          </div>
        </div>
      </div>


    </div>
  );
};

export default App; 